import React, { useEffect, useState } from "react";
import { Loader2, Trash2, Mail, MessageSquare, Globe2 } from "lucide-react";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { adminApi } from "@/lib/adminApi";
import AdminPageHeader from "@/components/admin/AdminPageHeader";

function fmtDate(iso) {
    try {
        return new Intl.DateTimeFormat("de-AT", { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
    } catch {
        return iso;
    }
}

export default function AdminContacts() {
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const load = () => {
        setLoading(true);
        adminApi
            .contacts({ limit: 200 })
            .then(setData)
            .catch(() => toast.error("Failed to load"))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const onDelete = async (id) => {
        setDeleting(id);
        try {
            await adminApi.deleteContact(id);
            toast.success("Deleted");
            setData((d) => ({ ...d, items: d.items.filter((x) => x.id !== id), total: d.total - 1 }));
            if (selected?.id === id) setSelected(null);
        } catch {
            toast.error("Failed to delete");
        } finally {
            setDeleting(null);
        }
    };

    const onExport = async () => {
        setExporting(true);
        try {
            await adminApi.exportCsv("contacts");
        } catch {
            toast.error("Export failed");
        } finally {
            setExporting(false);
        }
    };

    return (
        <div data-testid="admin-contacts">
            <AdminPageHeader
                eyebrow="Admin · Inbox"
                title="Contact messages"
                count={data.total}
                onRefresh={load}
                onExport={onExport}
                exporting={exporting}
            />

            <div className="bg-white rounded-3xl ring-1 ring-caireen-ink/10 overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center py-24 text-caireen-ink/40">
                        <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                ) : data.items.length === 0 ? (
                    <div className="text-center py-20 font-sans text-sm text-caireen-ink/55">
                        <Mail className="w-8 h-8 mx-auto mb-3 text-caireen-ink/30" />
                        No contact messages yet.
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-caireen-cream/60">
                                <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">When</TableHead>
                                <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">Name</TableHead>
                                <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">Email</TableHead>
                                <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">Subject</TableHead>
                                <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">Lang</TableHead>
                                <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55 text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {data.items.map((c) => (
                                <TableRow key={c.id} data-testid={`contact-row-${c.id}`} className="hover:bg-caireen-cream/40 cursor-pointer" onClick={() => setSelected(c)}>
                                    <TableCell className="font-sans text-sm text-caireen-ink/70 whitespace-nowrap">{fmtDate(c.created_at)}</TableCell>
                                    <TableCell className="font-sans text-sm font-semibold">{c.name}</TableCell>
                                    <TableCell className="font-sans text-sm text-caireen-ink/70">{c.email}</TableCell>
                                    <TableCell className="font-sans text-sm text-caireen-ink/70 max-w-xs truncate">{c.subject || "—"}</TableCell>
                                    <TableCell>
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-caireen-ink/5 font-sans text-[10px] uppercase tracking-widest">
                                            <Globe2 className="w-3 h-3" />
                                            {c.language?.toUpperCase() || "—"}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                        <Button
                                            data-testid={`contact-delete-${c.id}`}
                                            variant="ghost"
                                            size="sm"
                                            disabled={deleting === c.id}
                                            onClick={() => onDelete(c.id)}
                                            className="text-caireen-red hover:bg-caireen-red/10 rounded-full"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>

            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">
                            {selected?.subject || "Message"}
                        </DialogTitle>
                        <DialogDescription className="font-sans">
                            From <strong>{selected?.name}</strong> · {selected?.email} · {selected && fmtDate(selected.created_at)}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="bg-caireen-cream/60 rounded-2xl p-5 font-sans text-sm whitespace-pre-wrap leading-relaxed text-caireen-ink/85">
                        <MessageSquare className="w-4 h-4 text-caireen-ink/40 mb-2" />
                        {selected?.message}
                    </div>
                    <DialogFooter>
                        <Button asChild variant="outline" className="rounded-full">
                            <a href={`mailto:${selected?.email}?subject=Re: ${encodeURIComponent(selected?.subject || "")}`}>
                                <Mail className="w-4 h-4 mr-2" />
                                Reply by email
                            </a>
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
